import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

const SENSOR_CONFIG_COLUMNS =
	'id, customer_id, sensor_id, sensor_location, delivery_enabled, realtime_enabled, show_alkalinity, show_ph, show_temperature, show_conductivity'

/**
 * Hook to fetch all sensors for a customer with their latest measurements.
 * Subscribes to Supabase Realtime for live INSERT updates on aggregated_measurements.
 */
export function useAllSensors(customerId) {
	const [sensors, setSensors] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const initialFetchDone = useRef(false)

	const fetchSensors = useCallback(async () => {
		if (!customerId) {
			setLoading(false)
			return
		}

		try {
			// Only show the loading spinner on the very first fetch —
			// subsequent refetches (e.g. triggered by sensor_config UPDATE) update
			// silently so the subscription channel is never torn down.
			if (!initialFetchDone.current) {
				setLoading(true)
			}
			setError(null)

			const { data: configs, error: configError } = await supabase
				.from('sensor_configs')
				.select(SENSOR_CONFIG_COLUMNS)
				.eq('customer_id', customerId)
				.eq('delivery_enabled', true)
				.eq('realtime_enabled', true)
				.order('sensor_location')

			if (configError) throw configError

			if (!configs || configs.length === 0) {
				setSensors([])
				return
			}

			const { data: latestRows, error: latestError } = await supabase.rpc(
				'get_latest_measurements',
				{ p_customer_id: customerId }
			)

			if (latestError) {
				console.warn('Error fetching latest measurements:', latestError)
			}

			const latestBySensor = new Map(
				(latestRows || []).map((row) => [row.sensor_id, row])
			)

			setSensors(
				configs.map((config) => ({
					...config,
					latestMeasurement: latestBySensor.get(config.sensor_id) || null,
				}))
			)
		} catch (err) {
			console.error('Error fetching sensors:', err)
			setError(err.message)
		} finally {
			initialFetchDone.current = true
			setLoading(false)
		}
	}, [customerId])

	// Initial fetch
	useEffect(() => {
		fetchSensors()
	}, [fetchSensors])

	// Subscribe to broadcast channels for each known sensor. The ingest-data
	// edge function publishes to "measurement:<customerId>:<sensorId>" which
	// fires reliably without any Realtime publication config. A separate CDC
	// channel is kept as a fallback for postgres_changes events.
	useEffect(() => {
		if (!customerId || sensors.length === 0) return

		const channels = []

		sensors.forEach((sensor) => {
			const ch = supabase
				.channel(`measurement:${customerId}:${sensor.sensor_id}`)
				.on('broadcast', { event: 'measurement' }, (payload) => {
					const data = payload.payload
					if (!data) return
					setSensors(prev => prev.map(s => {
						if (s.sensor_id !== data.sensor_id) return s
						return {
							...s,
							latestMeasurement: {
								...s.latestMeasurement,
								total_alkalinity_ppm_rolling_avg: data.total_alkalinity_ppm,
								ph: data.ph,
								temperature_c: data.temperature_c,
								conductivity_mscm: data.conductivity_mscm,
								timestamp: data.timestamp,
								sensor_id: data.sensor_id,
								customer_id: customerId,
							}
						}
					}))
				})
				.subscribe((status) => {
					if (status === 'SUBSCRIBED') {
						console.debug('[use-all-sensors] Broadcast channel subscribed', { sensor_id: sensor.sensor_id })
					}
				})
			channels.push(ch)
		})

		return () => {
			channels.forEach(ch => supabase.removeChannel(ch))
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- resubscribe only when sensor count changes, not on every measurement update
	}, [customerId, sensors.length])

	// CDC fallback + sensor_configs UPDATE listener
	useEffect(() => {
		if (!customerId) return

		const channel = supabase
			.channel(`cdc:all-measurements:${customerId}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'aggregated_measurements',
					filter: `customer_id=eq.${customerId}`,
				},
				(payload) => {
					setSensors(prev => prev.map(sensor => {
						if (sensor.sensor_id === payload.new.sensor_id) {
							return { ...sensor, latestMeasurement: payload.new }
						}
						return sensor
					}))
				}
			)
			.on(
				'postgres_changes',
				{
					event: 'UPDATE',
					schema: 'public',
					table: 'sensor_configs',
					filter: `customer_id=eq.${customerId}`,
				},
				() => {
					fetchSensors()
				}
			)
			.subscribe((status) => {
				if (status === 'SUBSCRIBED') {
					console.debug('[use-all-sensors] CDC channel subscribed')
				} else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
					console.warn('[use-all-sensors] CDC channel error:', status)
				}
			})

		return () => {
			supabase.removeChannel(channel)
		}
	}, [customerId, fetchSensors])

	return {
		sensors,
		loading,
		error,
		refetch: fetchSensors
	}
}
